"use client"

import { useState } from "react"
import { ChevronLeft, ChevronRight, Star } from "lucide-react"
import { cn } from "@/lib/utils"

type Testimonial = {
  name: string
  initials: string
  role: string
  quote: string
  highlight?: string
  rating?: number
}

const testimonials: Testimonial[] = [
  {
    name: "Rebecca H.",
    initials: "RH",
    role: "Homeschool mom of 2",
    quote: "My daughter went from crying over math to asking to do extra problems. The teacher-led videos make all the difference — she finally feels like someone is actually teaching her.",
    highlight: "from crying over math to asking to do extra problems",
    rating: 5,
  },
  {
    name: "David M.",
    initials: "DM",
    role: "Parent of 3, grades 7-10",
    quote: "We tried four different math curricula before UnLock Math. The mastery-spiral approach is the only one that stuck. My middle child caught up two grade levels in one year.",
    highlight: "caught up two grade levels in one year",
    rating: 5,
  },
  {
    name: "Sarah T.",
    initials: "ST",
    role: "Homeschool co-op leader",
    quote: "I recommend UnLock Math to every family in our co-op. The instant feedback and step-by-step solutions mean parents don't need to be math experts to help their kids succeed.",
    highlight: "parents don't need to be math experts",
    rating: 5,
  },
  {
    name: "Jennifer L.",
    initials: "JL",
    role: "Mom of a struggling 8th grader",
    quote: "My son has dyscalculia and traditional programs were a nightmare. UnLock Math's video lessons let him pause, rewind, and replay until he truly understands. His confidence has completely transformed.",
    highlight: "His confidence has completely transformed",
    rating: 5,
  },
  {
    name: "Michael P.",
    initials: "MP",
    role: "Parent of 9th grader",
    quote: "The way UnLock Math connects each topic to the next is brilliant. My son isn't just memorizing — he's actually building understanding. The progress dashboard keeps us both accountable.",
    highlight: "actually building understanding",
    rating: 5,
  },
  {
    name: "Lisa K.",
    initials: "LK",
    role: "Homeschool mom, grades 6 & 8",
    quote: "We've been using UnLock Math for three years now. Both kids test above grade level. The automatic grading saves me hours every week, and the detailed reports make portfolio reviews a breeze.",
    highlight: "Both kids test above grade level",
    rating: 5,
  },
  {
    name: "Chris D.",
    initials: "CD",
    role: "Dad of a 10th grader",
    quote: "I was skeptical about online math, but the teacher's energy in the videos won me over. You can tell she genuinely loves math and loves teaching. That enthusiasm is contagious.",
    highlight: "That enthusiasm is contagious",
    rating: 5,
  },
  {
    name: "Amanda R.",
    initials: "AR",
    role: "Former classroom teacher",
    quote: "As a former teacher, I'm picky about curriculum. UnLock Math has the rigor I demand but the warmth my daughter needs. The spiral review is perfectly paced — nothing gets forgotten.",
    highlight: "the rigor I demand but the warmth my daughter needs",
    rating: 5,
  },
  {
    name: "Tom W.",
    initials: "TW",
    role: "Parent of twins, grade 7",
    quote: "Having twins in the same grade but at different math levels was a challenge. UnLock Math lets each kid move at their own pace without comparison. Game changer for our household.",
    highlight: "each kid move at their own pace without comparison",
    rating: 5,
  },
]

export function TestimonialSlider() {
  const [page, setPage] = useState(0)
  const perPage = 3
  const totalPages = Math.ceil(testimonials.length / perPage)

  const prev = () => setPage((p) => (p === 0 ? totalPages - 1 : p - 1))
  const next = () => setPage((p) => (p === totalPages - 1 ? 0 : p + 1))

  const visible = testimonials.slice(page * perPage, page * perPage + perPage)

  return (
    <div className="mx-auto max-w-7xl">
      <div className="relative">
        {/* Cards */}
        <div
          key={page}
          className="grid gap-6 md:grid-cols-3 animate-in fade-in slide-in-from-right-4 duration-500"
        >
          {visible.map((t) => (
            <div
              key={t.name}
              className="flex flex-col rounded-2xl border border-border/60 bg-card p-7 shadow-sm transition-shadow duration-300 hover:shadow-md"
            >
              {/* Stars */}
              {t.rating && (
                <div className="mb-5 flex gap-1">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="size-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
              )}

              {/* Quote */}
              <blockquote className="flex-1 text-base leading-relaxed text-foreground/85">
                {t.highlight ? (
                  <>
                    {t.quote.split(t.highlight)[0]}
                    <strong className="font-semibold text-foreground">{t.highlight}</strong>
                    {t.quote.split(t.highlight)[1]}
                  </>
                ) : (
                  t.quote
                )}
              </blockquote>

              {/* Author */}
              <div className="mt-6 flex items-center gap-3 border-t border-border/40 pt-5">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                  {t.initials}
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-4">
            <button
              onClick={prev}
              aria-label="Previous testimonials"
              className="flex size-10 items-center justify-center rounded-full border border-border bg-card text-foreground/70 shadow-sm transition-all hover:border-primary hover:text-primary hover:shadow-md"
            >
              <ChevronLeft className="size-5" />
            </button>

            <div className="flex gap-2">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  aria-label={`Go to page ${i + 1}`}
                  className={cn(
                    "size-2.5 rounded-full transition-all",
                    i === page
                      ? "w-8 bg-primary"
                      : "bg-border hover:bg-foreground/30"
                  )}
                />
              ))}
            </div>

            <button
              onClick={next}
              aria-label="Next testimonials"
              className="flex size-10 items-center justify-center rounded-full border border-border bg-card text-foreground/70 shadow-sm transition-all hover:border-primary hover:text-primary hover:shadow-md"
            >
              <ChevronRight className="size-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
